import type { ExtractResult } from './briefblip';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function utcStamp(d = new Date()): string {
  return [
    d.getUTCFullYear(),
    pad(d.getUTCMonth() + 1),
    pad(d.getUTCDate()),
    'T',
    pad(d.getUTCHours()),
    pad(d.getUTCMinutes()),
    pad(d.getUTCSeconds()),
    'Z',
  ].join('');
}

function dateStamp(d: Date): string {
  return [d.getFullYear(), pad(d.getMonth() + 1), pad(d.getDate())].join('');
}

function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\r?\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');
}

function foldLine(line: string): string {
  const max = 73;
  if (line.length <= max) return line;
  const chunks: string[] = [];
  let rest = line;
  chunks.push(rest.slice(0, max));
  rest = rest.slice(max);
  while (rest.length > 0) {
    chunks.push(' ' + rest.slice(0, max - 1));
    rest = rest.slice(max - 1);
  }
  return chunks.join('\r\n');
}

function line(name: string, value: string): string {
  return foldLine(`${name}:${escapeText(value)}`);
}

function stableId(text: string): string {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

function parseMeetingDate(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function resultToIcs(result: ExtractResult): string {
  const now = utcStamp();
  const title = result.title || 'BriefBlip meeting';
  const meetingDate = parseMeetingDate(result.meeting_date);
  const uidBase = stableId([title, result.recap, now].join('|'));
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Blue Note Logic Inc.//BriefBlip//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:BriefBlip',
  ];

  if (meetingDate) {
    lines.push(
      'BEGIN:VEVENT',
      `UID:briefblip-${uidBase}-meeting@briefblip.com`,
      `DTSTAMP:${now}`,
      `DTSTART;VALUE=DATE:${dateStamp(meetingDate)}`,
      line('SUMMARY', title),
      line(
        'DESCRIPTION',
        [
          result.recap,
          result.location ? `Location: ${result.location}` : '',
          result.meeting_time ? `Time: ${result.meeting_time}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
      ),
      'END:VEVENT',
    );
  }

  result.action_items.forEach((item, index) => {
    const summary = item.owner ? `${item.text} (${item.owner})` : item.text;
    lines.push(
      'BEGIN:VTODO',
      `UID:briefblip-${uidBase}-todo-${index + 1}-${stableId(item.text)}@briefblip.com`,
      `DTSTAMP:${now}`,
      'STATUS:NEEDS-ACTION',
      line('SUMMARY', summary),
      line(
        'DESCRIPTION',
        [
          `Source meeting: ${title}`,
          item.owner ? `Owner: ${item.owner}` : '',
          result.recap ? `Recap: ${result.recap}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
      ),
      'END:VTODO',
    );
  });

  result.next_steps.forEach((step, index) => {
    lines.push(
      'BEGIN:VTODO',
      `UID:briefblip-${uidBase}-next-${index + 1}-${stableId(step)}@briefblip.com`,
      `DTSTAMP:${now}`,
      'STATUS:NEEDS-ACTION',
      line('SUMMARY', step),
      line('DESCRIPTION', `Source meeting: ${title}`),
      'END:VTODO',
    );
  });

  lines.push('END:VCALENDAR');
  return lines.join('\r\n') + '\r\n';
}
